import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ErrorBoundary } from './ErrorBoundary';
import { createPortal } from 'react-dom';
import { Menu, MessageSquarePlus, RotateCw, X } from 'lucide-react';
import { useAuth } from '../auth/useAuth';
import { useOrg } from '../org/useOrg';
import { fetchAccounts, fetchMyProjects } from '../api';
import { MAX_ACCOUNT_SLOTS } from '@aiai/shared';
import { getSlot, lowestFreeSlot, slotPath } from '../lib/accountSlot';
import { qk } from '../api/queryKeys';
import { FeedbackContext } from './FeedbackContext';
import { FeedbackModal } from './FeedbackModal';
import { JumpPalette } from './JumpPalette';
import { LaunchMissionProvider } from './LaunchMissionContext';
import { VersionBadge } from './VersionBadge';

// Primary nav is GLOBAL — Mission control/Activity/Projects/Prototypes aggregate across every
// workspace the user belongs to (you enter a workspace by opening an item).
const navItems = [
  { to: '/', label: 'Mission control', end: true },
  { to: '/activity', label: 'Activity', end: false },
  { to: '/projects', label: 'Projects', end: false },
  { to: '/prototypes', label: 'Prototypes', end: false },
];

// The account menu mixes global destinations (Account, Docs) with the
// active-workspace tools (Workspace settings, Metrics). The workspace items are
// only included when there's an active workspace to point them at.
const menuItems = (slug: string) => [
  ...(slug ? [{ to: `/w/${slug}/settings`, label: 'Workspace' }] : []),
  { to: '/settings', label: 'Account' },
  { to: '/docs', label: 'Docs' },
  ...(slug ? [{ to: `/w/${slug}/metrics`, label: 'Metrics' }] : []),
];

// Shared style for the header's right-side controls. Square 32px icon buttons
// that read as one tidy toolbar; individual buttons opt into a wider labelled
// form at `lg` (where there's room) by appending `lg:w-auto lg:px-*`.
const toolbarBtnClass =
  'flex h-8 w-8 items-center justify-center rounded-control border border-line-strong bg-surface-1 text-ink-muted transition-colors hover:bg-surface-2 hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60';

function Logo() {
  return (
    <Link to="/" className="flex items-center gap-2.5">
      <span className="grid h-7 w-7 place-items-center rounded-[5px] bg-ink text-surface-1">
        <RotateCw className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="font-display text-sm font-bold tracking-tight text-ink">
        aiai<span className="text-signal">·</span>loop
      </span>
    </Link>
  );
}

const ITEM =
  'block w-full rounded-[5px] px-2.5 py-1.5 text-left text-sm text-ink-muted transition-colors hover:bg-surface-3 hover:text-ink';

// Dropdown is rendered into document.body via a portal and positioned `fixed`
// from the trigger's bounding rect, mirroring OverflowMenu.tsx — so no future
// overflow-hidden/transform ancestor (e.g. the sticky header's backdrop blur)
// can clip it.
function AccountMenu() {
  const { user, logout, logoutAll } = useAuth();
  const [open, setOpen] = useState(false);
  const { data: loaded } = useQuery({
    queryKey: ['auth', 'accounts'],
    queryFn: () => fetchAccounts(),
    enabled: open,
    staleTime: 30_000,
  });
  const accounts = loaded ?? [];
  const slot = getSlot();
  const others = accounts.filter((a) => a.slot !== slot);
  const free = loaded ? lowestFreeSlot(loaded) : null;
  const [position, setPosition] = useState<{ top: number; right: number } | null>(null);
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      const insideTrigger = buttonRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScrollOrResize() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  function toggle() {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      setPosition({ top: r.bottom + 4, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  }

  const { activeOrg } = useOrg();
  const slug = activeOrg?.slug ?? '';
  const initial = (user?.email?.[0] ?? '?').toUpperCase();
  const allMenuItems = menuItems(slug);

  return (
    <div className="hidden sm:block">
      <button
        ref={buttonRef}
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
        className="grid h-8 w-8 place-items-center rounded-full border border-line-strong bg-surface-2 text-xs font-semibold text-ink-muted transition-colors hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
      >
        {initial}
      </button>
      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: position.top, right: position.right }}
            className="fixed z-30 w-52 animate-fade-in rounded-[8px] border border-line-strong bg-surface-1 p-1 shadow-lg"
          >
            {user && (
              <div className="border-b border-line px-2.5 pb-2 pt-1.5">
                <div className="truncate text-xs text-ink">{user.email}</div>
              </div>
            )}
            <div className="py-1">
              {allMenuItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  role="menuitem"
                  className={({ isActive }) =>
                    `flex items-center justify-between rounded-[5px] px-2.5 py-1.5 text-sm transition-colors ${
                      isActive
                        ? 'bg-surface-2 text-ink'
                        : 'text-ink-muted hover:bg-surface-3 hover:text-ink'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
            {others.length > 0 && (
              <div className="border-t border-line py-1">
                {others.map((a) => (
                  <a key={a.slot} role="menuitem" href={slotPath(a.slot, '/')} className={ITEM}>
                    {a.email}
                  </a>
                ))}
              </div>
            )}
            {loaded && (
              <div className="border-t border-line py-1">
                {free !== null ? (
                  <a role="menuitem" href={slotPath(free, '/login?add=1')} className={ITEM}>
                    Add another account
                  </a>
                ) : (
                  <>
                    <span
                      role="menuitem"
                      aria-disabled="true"
                      className="block w-full cursor-not-allowed rounded-[5px] px-2.5 py-1.5 text-left text-sm text-ink-faint"
                    >
                      Add another account
                    </span>
                    <p className="px-2.5 pb-1 text-xs text-ink-muted">
                      Signed in to the maximum of {MAX_ACCOUNT_SLOTS} accounts.
                    </p>
                  </>
                )}
              </div>
            )}
            <div className="border-t border-line pt-1">
              <button type="button" role="menuitem" onClick={logout} className={ITEM}>
                Sign out
              </button>
              {accounts.length > 1 && (
                <button type="button" role="menuitem" onClick={logoutAll} className={ITEM}>
                  Sign out of all accounts
                </button>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export function Layout() {
  const { user, logout } = useAuth();
  const { activeOrg } = useOrg();
  const slug = activeOrg?.slug ?? '';

  // The "Projects" nav item is hidden unless the user actually has access to a
  // repo project through some workspace (owner, or a grant). Basic/prototype-only
  // workspaces have none, so a prototype-only user never sees it. Shares the
  // `qk.myProjects` cache with ProjectsPage/Overview, so this is usually free.
  const myProjects = useQuery({
    queryKey: qk.myProjects,
    queryFn: fetchMyProjects,
    enabled: !!user,
    staleTime: 30_000,
  });
  const hasProjects = (myProjects.data?.length ?? 0) > 0;
  const primaryNav = hasProjects
    ? navItems
    : navItems.filter((item) => item.to !== '/projects');

  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const location = useLocation();
  // Mission control (/) is a full-bleed, viewport-height console; every other page is a centred column.
  const fullBleed = location.pathname === '/';

  // Close the mobile menu on navigation so it never lingers over the new page.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const openFeedback = useCallback(() => setFeedbackOpen(true), []);
  const feedbackContextValue = useMemo(() => ({ openFeedback }), [openFeedback]);

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-2.5 py-1.5 font-display text-xs uppercase tracking-[0.08em] transition-colors border-b-[3px] ${
      isActive ? 'border-b-ink text-ink' : 'border-b-transparent text-ink-muted hover:text-ink'
    }`;

  return (
    <FeedbackContext.Provider value={feedbackContextValue}>
      <LaunchMissionProvider>
        <div className="min-h-full">
          <header className="sticky top-0 z-30 border-b border-line bg-surface-0/80 backdrop-blur">
            <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Logo />
                  {/* Build version is useful on mobile too (e.g. confirming a
                      deploy landed from a phone), so it shows at every width. */}
                  <VersionBadge />
                </div>
                <nav aria-label="Primary" className="hidden items-center gap-1 sm:flex">
                  {primaryNav.map((item) => (
                    <NavLink key={item.to} to={item.to} end={item.end} className={navLinkClass}>
                      {item.label}
                    </NavLink>
                  ))}
                </nav>
              </div>
              <div className="flex items-center gap-2">
                <JumpPalette />
                <button
                  type="button"
                  className={`${toolbarBtnClass} lg:w-auto lg:gap-1.5 lg:px-2.5 lg:text-xs`}
                  aria-label="Send feedback to aiai-loop"
                  onClick={openFeedback}
                >
                  <MessageSquarePlus className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
                  <span className="hidden lg:inline">Send feedback to aiai-loop</span>
                </button>
                <AccountMenu />
                {/* Mobile: hamburger toggles the nav drawer below `sm`. */}
                <button
                  type="button"
                  className={`${toolbarBtnClass} sm:hidden`}
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                  aria-controls="mobile-nav"
                  onClick={() => setMenuOpen((v) => !v)}
                >
                  {menuOpen ? (
                    <X className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Menu className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
            </div>

            {/* Mobile nav drawer: primary + secondary destinations. */}
            {menuOpen && (
              <div
                id="mobile-nav"
                className="animate-fade-in border-t border-line bg-surface-0/95 backdrop-blur sm:hidden"
              >
                <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
                  {[...primaryNav, ...menuItems(slug).map((m) => ({ ...m, end: false }))].map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `flex items-center justify-between rounded-[5px] px-3 py-2.5 text-sm transition-colors ${
                          isActive
                            ? 'bg-surface-2 text-ink font-medium'
                            : 'text-ink-muted hover:bg-surface-2 hover:text-ink'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  <div className="mt-2 flex items-center justify-between gap-3 border-t border-line pt-3">
                    {user && (
                      <span className="min-w-0 truncate text-xs text-ink-muted">{user.email}</span>
                    )}
                    <a href="/accounts" className="shrink-0 text-xs text-ink-muted hover:text-ink">
                      Switch account
                    </a>
                    <button type="button" className="btn-secondary btn-sm shrink-0" onClick={logout}>
                      Sign out
                    </button>
                  </div>
                </nav>
              </div>
            )}
          </header>
          <main className={fullBleed ? 'lg:h-[calc(100dvh-3.5rem-1px)] lg:overflow-hidden' : 'mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8'}>
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
          <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        </div>
      </LaunchMissionProvider>
    </FeedbackContext.Provider>
  );
}
